/**
 * ============================================================================
 * DIALOGS STORE - FETCH OPERATIONS
 * ============================================================================
 * 
 * Handles fetching dialogs from the backend API.
 * 
 * @module dialogs/api/fetch
 */

import { apiService } from "@/lib/api-functions/api-service";
import { dialogStore } from "../store";
import type { Dialog, ToastFunction } from "../types";
import { migrateDialogSectionsToNodes, deduplicateSectionIdentifiers } from "@/components/wizards/dialog/utils/dialogUtils";

// ============================================================================
// FETCH ALL DIALOGS
// ============================================================================

/**
 * Fetch dialogs for a specific game from the API and sync the store.
 * 
 * @param gameId - The game ID to fetch dialogs for
 * @param folder - Optional folder ID to filter items by folder
 * @param toast - Optional toast function for error reporting
 * @returns Promise resolving to array of dialogs
 * 
 * @example
 * ```ts
 * const dialogs = await fetchDialogs(gameId);
 * ```
 */
export async function fetchDialogs(
  gameId: string,
  folder?: string | null,
  toast?: ToastFunction
): Promise<Dialog[]> {
  try {
    // Validate authentication
    const token = await apiService.getToken();
    if (!token) {
      throw new Error('No authentication token available');
    }

    // Fetch data from API
    const result = await apiService.fetchDialogs(gameId, folder);
    
    if (!result.success || !result.data) {
      throw new Error(result.error || 'Failed to fetch dialogs');
    }

    let dialogs = result.data as unknown as Dialog[];
    
    // Apply deduplication and migration
    dialogs = dialogs.map(dialog => {
      if (dialog.data) {
        // First deduplicate section identifiers
        const deduplicatedData = {
          ...dialog.data,
          sections: dialog.data.sections ? deduplicateSectionIdentifiers(dialog.data.sections) : undefined,
        };
        // Then apply migration from sections to nodes
        return {
          ...dialog,
          data: migrateDialogSectionsToNodes(deduplicatedData),
        };
      }
      return dialog;
    });
    
    // Sync the store with the fetched data
    await dialogStore.syncFromRemote(async () => dialogs);
    
    return dialogs;
    
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Failed to fetch dialogs';
    console.error(`[fetchDialogs] Error for gameId: ${gameId}:`, err);
    
    if (toast) {
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
    }
    
    throw new Error(message);
  }
}

// ============================================================================
// FETCH SINGLE DIALOG
// ============================================================================

/**
 * Fetch a single dialog with full details (sections and options).
 * 
 * Use this when you need the complete dialog data, not just the list item.
 * 
 * @param dialogId - The dialog ID to fetch
 * @param gameId - The game ID
 * @param toast - Optional toast function for error reporting
 * @returns Promise resolving to the dialog
 * 
 * @example
 * ```ts
 * const dialog = await fetchDialog(dialogId, gameId);
 * ```
 */
export async function fetchDialog(
  dialogId: string,
  gameId: string,
  toast?: ToastFunction
): Promise<Dialog> {
  try {
    const token = await apiService.getToken();
    if (!token) {
      throw new Error('No authentication token available');
    }

    const result = await apiService.fetchDialog(dialogId, gameId);
    
    if (!result.success || !result.data) {
      throw new Error(result.error || 'Failed to fetch dialog');
    }

    let dialog = result.data as Dialog;
    
    // Apply deduplication and migration
    if (dialog.data) {
      // First deduplicate section identifiers
      const deduplicatedData = {
        ...dialog.data,
        sections: dialog.data.sections ? deduplicateSectionIdentifiers(dialog.data.sections) : undefined,
      };
      // Then apply migration from sections to nodes
      dialog = {
        ...dialog,
        data: migrateDialogSectionsToNodes(deduplicatedData),
      };
    }

    return dialog;
    
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Failed to fetch dialog';
    console.error(`[fetchDialog] Error for dialogId: ${dialogId}:`, err);
    
    if (toast) {
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
    }
    
    throw new Error(message);
  }
}
